import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import MobileHeader from './MobileHeader';
import MobileBottomNav from './MobileBottomNav';
import MobileSidebar from './MobileSidebar';

const MobileLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const location = useLocation();
  const baselineViewportHeightRef = useRef(null);

  // PWA viewport height fix for mobile
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // Detect virtual keyboard open/close so we can hide bottom nav during typing.
  useEffect(() => {
    const getViewportHeight = () =>
      window.visualViewport?.height || window.innerHeight;

    const isTextInputElement = (element) => {
      if (!element) return false;
      const tagName = element.tagName?.toLowerCase();
      if (tagName === 'textarea') return true;
      if (tagName === 'input') {
        const inputType = (element.getAttribute('type') || 'text').toLowerCase();
        return !['checkbox', 'radio', 'button', 'submit', 'reset', 'file'].includes(inputType);
      }
      return !!element.isContentEditable;
    };

    const updateKeyboardState = () => {
      const currentHeight = getViewportHeight();
      if (!baselineViewportHeightRef.current) {
        baselineViewportHeightRef.current = currentHeight;
      }

      const baseline = baselineViewportHeightRef.current;
      const heightDelta = baseline - currentHeight;
      const focusedTextInput = isTextInputElement(document.activeElement);

      const open = heightDelta > 140 || (focusedTextInput && heightDelta > 80);
      setKeyboardOpen(open);
    };

    const resetBaseline = () => {
      baselineViewportHeightRef.current = getViewportHeight();
      updateKeyboardState();
    };

    baselineViewportHeightRef.current = getViewportHeight();

    const handleFocusOut = () => setTimeout(updateKeyboardState, 50);

    window.addEventListener('resize', resetBaseline);
    window.visualViewport?.addEventListener('resize', updateKeyboardState);
    window.addEventListener('focusin', updateKeyboardState);
    window.addEventListener('focusout', handleFocusOut);

    return () => {
      window.removeEventListener('resize', resetBaseline);
      window.visualViewport?.removeEventListener('resize', updateKeyboardState);
      window.removeEventListener('focusin', updateKeyboardState);
      window.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('keyboard-open', keyboardOpen);
    return () => {
      document.documentElement.classList.remove('keyboard-open');
    };
  }, [keyboardOpen]);

  const handleMenuClick = () => {
    setSidebarOpen(true);
  };

  const handleCommunitySelect = () => {
    // Handle community selection
    console.log('Community selector clicked');
  };

  const isChatRoute = location.pathname === '/chat';

  return (
    <div
      className="lg:hidden min-h-screen bg-gray-50"
      style={{ minHeight: 'calc(var(--vh, 1vh) * 100)' }}
    >
      {/* Mobile Header */}
      {!isChatRoute && (
        <MobileHeader
          title="FieldHouse"
          onMenuClick={handleMenuClick}
          onCommunitySelect={handleCommunitySelect}
        />
      )}

      {/* Main Content - Add padding top to account for fixed header */}
      <main 
        className={isChatRoute ? (keyboardOpen ? 'pb-2' : 'pb-24') : 'pb-14'}
        style={{
          paddingTop: isChatRoute
            ? 'env(safe-area-inset-top, 0px)'
            : `calc(env(safe-area-inset-top, 0px) + 4.5rem)` // safe area + header height (~4.5rem for py-3 + content)
        }}
      >
        <div className={isChatRoute ? '' : 'px-4 py-4'}>
          <Outlet />
        </div>
      </main>

      {/* Bottom Navigation */}
      {!(isChatRoute && keyboardOpen) && <MobileBottomNav />}

      {/* Mobile Sidebar */}
      <MobileSidebar 
        sidebarOpen={sidebarOpen} 
        setSidebarOpen={setSidebarOpen} 
      />
    </div>
  );
};

export default MobileLayout;
